/**
 * Progressive enhancement for the TCS Shopify-native filter presentation.
 *
 * Shopify/Horizon owns filter state, URL creation, section rendering and
 * analytics events through facets-form-component. This element owns only the
 * inline panel state, the native sort-select handoff and enhanced filter links.
 */

import { StandardEvents } from '@shopify/events';

const panelState = new Map();

// Set on every filter update and absent from the server markup, so a section
// morph that lands removes it. Horizon's renderSection() resolves even when the
// response holds no section (a non-2xx body): morphSection() is async and its
// rejection is not awaited. A marker still present after the update resolves
// therefore means nothing was rendered — the URL and inputs show the new
// filters while the results do not (C03, 2026-09-18).
const RENDER_PENDING = 'data-tcs-render-pending';
const RELOAD_NOTICE_KEY = 'tcs.filters.reload_notice';
const RELOAD_NOTICE_STATE = 'tcsFilterReloadNotice';

// True from the moment a reload with a notice is requested until the document
// goes away, so a reconnect in between cannot consume the notice it just stored.
// A document restored from the back/forward cache starts clean again.
let reloadRequested = false;
window.addEventListener('pageshow', (event) => {
  if (event.persisted) reloadRequested = false;
});

/** The URL a stored notice belongs to: path and query, not the fragment. */
const noticeUrl = () => window.location.pathname + window.location.search;

/** True unless the browser reports this document was reached some other way than a reload. */
const isReload = () => {
  const [entry] = performance.getEntriesByType?.('navigation') ?? [];
  return !entry || entry.type === 'reload';
};

/** A stored record's notice, or '' unless it was stored for this URL. */
const noticeFor = (record) => {
  if (!record || typeof record !== 'object' || record.url !== noticeUrl()) return '';
  return typeof record.notice === 'string' ? record.notice : '';
};

class TcsFilters extends HTMLElement {
  #toggle;
  #panel;
  #facetsForm;
  #fallbackApply;
  #fallbackTimer;
  #updateVersion = 0;
  #fragmentLink = null;
  #fragmentTimer;
  // The facet form as the server last rendered it (page load or a landed
  // update). Shopify re-renders prices formatted ("15.00") while the URL keeps
  // what was typed ("15"), so an unedited form can differ from the URL as text.
  #appliedForm = null;
  // The reload notice this element is showing; '' once a filter update starts.
  #reloadNotice = '';
  #observer = new MutationObserver(() => {
    this.#enhance();
    this.#assertReloadNotice();
  });

  connectedCallback() {
    this.#enhance();
    this.#appliedForm = this.#formState();
    this.#showReloadNotice();
    this.#observer.observe(this, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
    this.addEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);
    window.addEventListener('popstate', this.#onPopState);
    window.addEventListener('pageshow', this.#syncSortDisplay);
    document.addEventListener('click', this.#onDocumentClick, true);
    window.addEventListener('click', this.#onClickSettled);
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    window.clearTimeout(this.#fallbackTimer);
    window.clearTimeout(this.#fragmentTimer);
    this.#fragmentLink = null;
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
    this.removeEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);
    window.removeEventListener('popstate', this.#onPopState);
    window.removeEventListener('pageshow', this.#syncSortDisplay);
    document.removeEventListener('click', this.#onDocumentClick, true);
    window.removeEventListener('click', this.#onClickSettled);
  }

  #enhance() {
    this.#syncSortDisplay();
    this.#toggle = this.querySelector('[data-tcs-filter-toggle]');
    this.#panel = this.querySelector('[data-tcs-filter-panel]');
    this.#facetsForm = this.querySelector('facets-form-component');
    this.#fallbackApply = this.querySelector('[data-tcs-filter-apply]');

    if (!(this.#toggle instanceof HTMLButtonElement) || !(this.#panel instanceof HTMLElement))
      return;

    if (this.#toggle.hidden) this.#toggle.hidden = false;
    if (this.#fallbackApply instanceof HTMLButtonElement && !this.#fallbackApply.hidden) {
      this.#fallbackApply.hidden = true;
    }

    this.#setOpen(panelState.get(this.#stateKey) ?? false);
  }

  get #stateKey() {
    return this.dataset.sectionId ?? 'results';
  }

  #setOpen(isOpen) {
    if (!(this.#toggle instanceof HTMLButtonElement) || !(this.#panel instanceof HTMLElement))
      return;

    panelState.set(this.#stateKey, isOpen);
    const expanded = String(isOpen);
    if (this.#toggle.getAttribute('aria-expanded') !== expanded) {
      this.#toggle.setAttribute('aria-expanded', expanded);
    }
    if (this.#panel.hidden === isOpen) this.#panel.hidden = !isOpen;
    if (this.hasAttribute('data-panel-open') !== isOpen) {
      this.toggleAttribute('data-panel-open', isOpen);
    }
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-tcs-filter-toggle]');
    if (toggle === this.#toggle) {
      this.#setOpen(this.#toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const link = event.target.closest('[data-tcs-filter-url]');
    if (
      !(link instanceof HTMLAnchorElement) ||
      !this.#facetsForm ||
      !('updateFiltersByURL' in this.#facetsForm)
    )
      return;

    // Clear all removes its own row. For keyboard and assistive-technology
    // activation (a synthetic click with detail 0), move focus to the surviving
    // Filters button before the section morph. The result-count status then
    // changes after focus. Pointer activation keeps the existing recovery path.
    if (
      link.matches('.tcs-filters__clear-all') &&
      event instanceof MouseEvent &&
      event.detail === 0 &&
      document.activeElement === link &&
      this.#toggle instanceof HTMLButtonElement
    ) {
      this.#toggle.focus({ preventScroll: true });
    }

    event.preventDefault();
    try {
      this.#facetsForm.updateFiltersByURL(link.href);
    } catch {
      window.location.assign(link.href);
    }
  };

  // The sort control's visible text is an aria-hidden display label (the
  // native select sits transparent over it at the 16px iOS focus-zoom floor;
  // see blocks/tcs-filters.liquid). Mirror the selected option into it: on
  // change, after every morph (#enhance) and when the browser restores form
  // state on back/forward (pageshow). Writes only on a difference, so the
  // childList observer that calls #enhance cannot loop.
  #syncSortDisplay = () => {
    const select = this.querySelector('[data-tcs-filter-sort]');
    const display = this.querySelector('[data-tcs-filter-sort-display]');
    if (!(select instanceof HTMLSelectElement) || !(display instanceof HTMLElement)) return;
    const text = select.selectedOptions[0]?.textContent.replace(/\s+/g, ' ').trim();
    if (text && display.textContent !== text) display.textContent = text;
  };

  #onChange = (event) => {
    if (
      !(event.target instanceof HTMLSelectElement) ||
      !event.target.matches('[data-tcs-filter-sort]')
    )
      return;
    this.#syncSortDisplay();
    if (!this.#facetsForm || !('updateFilters' in this.#facetsForm)) return;

    this.#facetsForm.updateFilters();
  };

  // C03-F4: the no-JS Apply button stays in the form. The server renders it
  // `hidden` (with the panel collapsed; blocks/tcs-filters.liquid shows both
  // only under <noscript>), yet it remains the form's default button, so Enter
  // in a price field submits natively: a full page load that closes the panel
  // and drops focus to the top. Horizon's own filter form has no submit
  // button, so implicit submission is blocked there and facets.js never
  // handles submit.
  // Once facets.js is ready, route the submit through its fragment path.
  // Browsers differ: Chromium fires `change` before submitting (the update is
  // already under way), WebKit does not. Update only when the form still
  // differs from the URL, so one Enter makes one request. Without JS, or
  // before facets.js defines the form component, the native GET still runs.
  #onSubmit = (event) => {
    const facetsForm = this.#facetsForm;
    if (
      !(event.target instanceof HTMLFormElement) ||
      !facetsForm?.contains(event.target) ||
      !('updateFilters' in facetsForm) ||
      !('createURLParameters' in facetsForm)
    )
      return;

    event.preventDefault();

    // Nothing edited since the last render (a second Enter, or Enter on a
    // shared link that already carries the price): no request, no history.
    if (this.#formState() === this.#appliedForm) return;

    const sorted = (params) => [...params].map(String).sort().join('&');
    const current = new URLSearchParams(window.location.search);
    current.delete('page');
    if (sorted(facetsForm.createURLParameters()) === sorted(current)) return;

    // Implicit submission reports the hidden Apply button as the submitter,
    // so read the field from focus: a price field goes through Horizon's
    // price path, which also clamps the value to the allowed range.
    const price = event.target.querySelector('price-facet-component');
    const focused = document.activeElement;
    if (price && focused && price.contains(focused) && 'updatePriceFilterAndResults' in price) {
      price.updatePriceFilterAndResults();
    } else {
      facetsForm.updateFilters();
    }
  };

  /** Non-empty facet form fields, order-independent; null without a form. */
  #formState() {
    const form = this.#facetsForm?.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return null;
    return [...new FormData(form)]
      .filter(([name, value]) => name !== 'page' && String(value).trim() !== '')
      .map(([name, value]) => `${name}=${String(value).trim()}`)
      .sort()
      .join('&');
  }

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || this.#toggle?.getAttribute('aria-expanded') !== 'true') return;

    event.preventDefault();
    this.#setOpen(false);
    this.#toggle?.focus();
  };

  #onFilterUpdate = (event) => {
    if (!('promise' in event)) return;
    this.#fragmentLink = null;
    // The next render owns the count again.
    this.#reloadNotice = '';

    const focusedControl = this.contains(document.activeElement) ? document.activeElement : null;
    const updateVersion = ++this.#updateVersion;
    this.setAttribute(RENDER_PENDING, '');
    window.clearTimeout(this.#fallbackTimer);
    this.#fallbackTimer = window.setTimeout(() => {
      if (updateVersion === this.#updateVersion) this.#reloadWithNotice();
    }, 8000);

    Promise.resolve(event.promise).then(
      () => {
        if (updateVersion !== this.#updateVersion) return;
        window.clearTimeout(this.#fallbackTimer);
        this.#appliedForm = this.#formState();

        // No render landed: load the URL the update already pushed, natively.
        if (this.isConnected && this.hasAttribute(RENDER_PENDING)) {
          this.#reloadWithNotice();
          return;
        }

        // Removal chips disappear; Clear all survives inside a hidden row.
        // Recover unavailable focus only, never steal it if the shopper moved
        // elsewhere in flight. Surviving inputs keep their native focus.
        if (
          this.isConnected &&
          focusedControl &&
          (!focusedControl.isConnected || focusedControl.closest('[hidden]')) &&
          (document.activeElement === document.body || document.activeElement === focusedControl)
        ) {
          const replacement = focusedControl.id
            ? this.querySelector(`#${CSS.escape(focusedControl.id)}`)
            : null;
          const target =
            replacement instanceof HTMLElement && !replacement.closest('[hidden]')
              ? replacement
              : this.#toggle;
          target?.focus({ preventScroll: true });
        }
      },
      () => {
        if (updateVersion === this.#updateVersion) this.#reloadWithNotice();
      },
    );
  };

  #reloadWithNotice() {
    reloadRequested = true;
    this.#storeReloadNotice();
    window.location.reload();
  }

  // The notice is stored with the URL it was set for and is single-use: the
  // next load that runs this file removes it, and shows it only when that load
  // is the reload of that same URL.
  #storeReloadNotice() {
    const notice = this.dataset.reloadNotice?.trim();
    if (!notice) return;
    const record = { notice, url: noticeUrl() };
    try {
      sessionStorage.setItem(RELOAD_NOTICE_KEY, JSON.stringify(record));
    } catch {
      // Storage may be blocked; the consistency-preserving reload still runs.
    }
    try {
      const state = history.state && typeof history.state === 'object' ? history.state : {};
      history.replaceState({ ...state, [RELOAD_NOTICE_STATE]: record }, '', location.href);
    } catch {
      // History state can be unavailable in unusual embedded contexts.
    }
  }

  #showReloadNotice() {
    if (reloadRequested) return;

    let notice = '';
    try {
      const stored = sessionStorage.getItem(RELOAD_NOTICE_KEY);
      sessionStorage.removeItem(RELOAD_NOTICE_KEY);
      if (stored) notice = noticeFor(JSON.parse(stored));
    } catch {
      // No stored notice in storage-restricted contexts, or not a record.
    }
    try {
      if (
        history.state &&
        typeof history.state === 'object' &&
        RELOAD_NOTICE_STATE in history.state
      ) {
        const { [RELOAD_NOTICE_STATE]: record, ...state } = history.state;
        history.replaceState(state, '', location.href);
        notice ||= noticeFor(record);
      }
    } catch {
      // Leave the page alone if history state cannot be rewritten.
    }
    if (!notice || !isReload()) return;

    this.#reloadNotice = notice;
    this.#assertReloadNotice();
  }

  // Writes the notice into the count's status region. Runs on connect and from
  // the mutation observer, so a rewrite of the region before the next filter
  // update is answered when it happens. Writing only on a difference keeps the
  // live region from announcing the same text twice and the observer from looping.
  #assertReloadNotice() {
    const notice = this.#reloadNotice;
    if (!notice) return;
    const status = this.querySelector('[role="status"][data-testid="products-count"]');
    if (!(status instanceof HTMLElement) || status.textContent === notice) return;
    status.classList.add('tcs-filters__results-count--notice');
    status.textContent = notice;
  }

  // Remember a same-document fragment link (e.g. "Skip to results list") at
  // activation. Following it fires popstate with only the hash changed; the
  // rendered results still match the URL, so reloading would only discard the
  // shopper's position (A3-01). Browsers deliver that popstate either during
  // the click or shortly after it (Firefox), hence the short window. The record
  // is dropped for cancelled or modified clicks, on the first popstate, on any
  // filter update and after 1 s, so filter and infinite-scroll back/forward
  // history still reloads. Links in shadow DOM, SVG <a> and <area> are not
  // recognised and keep the reload.
  #onDocumentClick = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !link.hash) return;
    if (
      link.origin !== window.location.origin ||
      link.pathname !== window.location.pathname ||
      link.search !== window.location.search
    )
      return;

    this.#fragmentLink = {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: link.hash,
    };
    window.clearTimeout(this.#fragmentTimer);
    this.#fragmentTimer = window.setTimeout(() => {
      this.#fragmentLink = null;
    }, 1000);
  };

  // Bubble phase on window runs after page handlers: a cancelled click navigates nowhere.
  #onClickSettled = (event) => {
    if (event.defaultPrevented) this.#fragmentLink = null;
  };

  #onPopState = () => {
    const fragment = this.#fragmentLink;
    this.#fragmentLink = null;
    window.clearTimeout(this.#fragmentTimer);

    if (
      fragment &&
      window.location.pathname === fragment.pathname &&
      window.location.search === fragment.search &&
      window.location.hash === fragment.hash
    )
      return;

    window.location.reload();
  };
}

if (!customElements.get('tcs-filters')) {
  customElements.define('tcs-filters', TcsFilters);
}
